/**
 * The one place a filmstrip column becomes a timestamp, and back.
 *
 * The timeline used to hold two time↔pixel mappings that disagreed: cells derived
 * their frame's time from their index over the column count, while the needle, the
 * moment head and scroll-centring mapped time to pixels directly. Those agree only
 * when `timelineWidth` is an exact multiple of `frameWidth` — and the column count
 * is a `ceil` — so every cell showed a frame from slightly before its own position,
 * accumulating across the strip to nearly a full column at the right-hand end.
 *
 * Deriving both directions from the column's real pixel offset makes them one
 * mapping. Kept free of React and DOM so the arithmetic can be tested directly;
 * `__tests__/timelineGeometry.test.ts` pins the round-trip property and the drift
 * the old mapping produced.
 */

export type TimelineGeometry = {
  /** Width of a single column in pixels (the last one may be narrower). */
  frameWidth: number;
  /** Full scrollable width of the strip in pixels. */
  timelineWidth: number;
  /** Media duration in seconds. */
  duration: number;
};

/**
 * Timestamp shown by the column at `index`, sampled at the column's left edge.
 *
 * Left-edge sampling is the filmstrip convention: a column labels the instant it
 * begins, so the frame under the playhead is from up to one column-duration
 * earlier. That is expected. What was not expected is the column's frame not
 * matching its own position, which is what this fixes.
 */
export function timeAtColumn(index: number, geometry: TimelineGeometry): number {
  const { frameWidth, timelineWidth, duration } = geometry;
  if (!timelineWidth || !duration || timelineWidth <= 0 || duration <= 0) return 0;
  const clampedIndex = Math.max(0, index);
  const time = ((clampedIndex * frameWidth) / timelineWidth) * duration;
  // A column can only start past the end if the caller asked for one that does not
  // exist; clamp so no caller ever seeks beyond the media.
  return Math.min(time, duration);
}

/**
 * Inverse of {@link timeAtColumn} — the pixel offset a timestamp sits at. Used by
 * the needle, the moment head and scroll-centring, so that the two directions of
 * the mapping cannot drift apart again.
 *
 * Takes only the two fields it needs, so callers that have no notion of a column
 * (the needle) don't have to invent a `frameWidth` to satisfy the type.
 */
export function pixelAtTime(
  time: number,
  geometry: Pick<TimelineGeometry, 'timelineWidth' | 'duration'>,
): number {
  const { timelineWidth, duration } = geometry;
  if (!duration || duration <= 0) return 0;
  return (time / duration) * timelineWidth;
}

/**
 * Inverse of {@link pixelAtTime} — the timestamp a pixel offset sits at. Callers that need this
 * direction (the trim handles' visible window) should call it rather than re-deriving
 * `duration / timelineWidth` themselves, for the same reason `pixelAtTime` exists: two
 * independent copies of this ratio are how the timeline drifted before.
 */
export function timeAtPixel(
  pixel: number,
  geometry: Pick<TimelineGeometry, 'timelineWidth' | 'duration'>,
): number {
  const { timelineWidth, duration } = geometry;
  if (!timelineWidth || timelineWidth <= 0) return 0;
  return (pixel / timelineWidth) * duration;
}
